/**
 * app-version — the client half of update control.
 *
 * Resolves the running app's identity (platform + store + build + version) once,
 * stamps it on every API request (so the server can enforce a minimum build),
 * and asks the server whether this build must / should update.
 *
 * Everything is keyed by the (platform, store) bucket because the three stores
 * run independent version lines. Native-only: on web this is inert (no headers,
 * no check). Fail-open everywhere — a check that errors, times out, or runs on an
 * unknown store must NEVER block the app (offline-first).
 */
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { api, setAppHeadersProvider } from "@/lib/api-client";

export type AppStore = "appstore" | "play" | "huawei";
export interface AppIdentity {
  platform: "ios" | "android";
  store: AppStore;
  build: number | null;   // Android versionCode / iOS CFBundleVersion
  version: string;        // marketing version (used for iOS compare)
}

// Play ships the default applicationId; the Huawei flavor overrides it (see
// android/app/build.gradle productFlavors).
const HUAWEI_ID = "com.elementree.iCheck";

let cached: AppIdentity | null = null;
let initPromise: Promise<AppIdentity | null> | null = null;

function resolveStore(platform: string, id: string): AppStore | null {
  if (platform === "ios") return "appstore";
  if (platform === "android") return id === HUAWEI_ID ? "huawei" : "play";
  return null; // web / unknown
}

/** Sync header accessor for the API client. Empty until the identity resolves —
 *  a few very-early requests may omit the headers, which is fine (the version
 *  check is the authoritative gate; header enforcement is best-effort). */
export function appIdentityHeaders(): Record<string, string> {
  if (!cached) return {};
  const h: Record<string, string> = {
    "X-App-Platform": cached.platform,
    "X-App-Store": cached.store,
    "X-App-Version": cached.version,
  };
  if (cached.build != null) h["X-App-Build"] = String(cached.build);
  return h;
}

/** Resolve + cache the app identity and register the header provider. Idempotent;
 *  call once at startup. No-op (returns null) on web / unknown store. */
export async function initAppIdentity(): Promise<AppIdentity | null> {
  if (cached) return cached;
  if (!Capacitor.isNativePlatform()) return null;
  if (!initPromise) {
    // Register eagerly so requests get headers the moment the identity lands.
    setAppHeadersProvider(appIdentityHeaders);
    initPromise = (async () => {
      try {
        const info = await App.getInfo(); // { id, name, build, version }
        const platform = Capacitor.getPlatform() as "ios" | "android";
        const store = resolveStore(platform, info.id);
        if (!store) return null;
        const buildNum = Number(info.build);
        cached = {
          platform,
          store,
          build: Number.isFinite(buildNum) ? buildNum : null,
          version: info.version,
        };
        return cached;
      } catch {
        return null; // fail-open
      }
    })();
  }
  return initPromise;
}

export interface UpdateStatus {
  forceUpgrade: boolean;
  recommendUpgrade: boolean;
  latestVersion: string | null;
  storeUrl: string | null;
  message: string | null;
}
const NO_UPDATE: UpdateStatus = {
  forceUpgrade: false, recommendUpgrade: false, latestVersion: null, storeUrl: null, message: null,
};

/** Ask the server whether this build must / should update. Fail-open on any
 *  error / offline / non-native / unknown store. Short timeout so a slow or down
 *  endpoint never delays or blocks the app. */
export async function getUpdateStatus(): Promise<UpdateStatus> {
  const id = await initAppIdentity();
  if (!id) return NO_UPDATE;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), 4000);
  try {
    const data = await api.one<UpdateStatus>("/api/app-version", {
      anonymous: true,
      signal: ctrl.signal,
      query: {
        platform: id.platform,
        store: id.store,
        version: id.version,
        build: id.build ?? undefined,
      },
    });
    return { ...NO_UPDATE, ...data };
  } catch {
    return NO_UPDATE; // never block on a failed check
  } finally {
    clearTimeout(timer);
  }
}

/** Open the store listing so the user can update. */
export function openStore(url: string | null): void {
  if (!url) return;
  try { window.open(url, "_blank"); } catch { /* best-effort */ }
}
